/* eslint-disable @typescript-eslint/camelcase */
import intl from 'react-intl-universal'
import { cloneDeep, set } from 'lodash'

import {
  getRemoveEmptyParagraphs,
  getLineSettings,
} from '~/redux/settingsReducer'
import {
  getDraftEditorContentState,
  getHeadlineCurrentContentState,
  getSubheadlineCurrentContentState,
  clipboardContent,
  relatedContent,
} from '~/redux/draftEditor'
import { getStoryData } from '~/redux/storyData'
import {
  draftContentStateToAns,
  draftRelatedContentToAns,
} from '~/reactapp/story/draft-to-ans'
import storyDataToAns from '~/reactapp/story/export/storyDataToAns'
import {
  getWordCount,
  getLineCount,
  getInches,
  getCharacterCount,
  getTextWithoutComments,
} from '~/reactapp/story/util/draftUtils'
import {
  Story,
  ComposerSettings,
  EllipsisReduxStore,
  Circulation,
} from '~/types'
import handleRequired from './handleRequired'

type ValidateStory = Promise<{
  errorMessages: string[]
  warningMessages: string[]
}>

const exportClipboardContent = (clipboardContent) => {
  if (!clipboardContent) {
    return {}
  }
  return Object.keys(clipboardContent)
    .filter(
      (key) => !!clipboardContent[key] && clipboardContent[key].length > 0
    )
    .reduce((acc, key) => {
      acc[key] = clipboardContent[key].map((item) =>
        item.get('clipboardANS').toJS()
      )
      return acc
    }, {})
}

export function makeStoryAnsFromReduxState(
  story: Story,
  state: EllipsisReduxStore,
  options: { setHeadlines: boolean; isMutableStory: boolean } = {
    setHeadlines: false,
    isMutableStory: false,
  }
) {
  let storyClone

  if (options.isMutableStory) {
    // directly mutate storyObj from Angular's story service
    storyClone = story
  } else {
    storyClone = cloneDeep(story)
  }

  const removeEmptyParagraphs = getRemoveEmptyParagraphs(state)
  const { contentElements, headlines, subheadlines } = draftContentStateToAns(
    {
      contentElements: {
        contentState: getDraftEditorContentState(state),
      },
      headlines: {
        contentState: getHeadlineCurrentContentState(state),
      },
      subheadlines: {
        contentState: getSubheadlineCurrentContentState(state),
      },
    },
    {
      removeEmptyParagraphs,
    }
  )

  if (!contentElements) {
    throw new Error(intl.get('epics.saveStoryEpic.errors.noContentState'))
  }

  // Weird story api, update content_elements straight in shared story instance
  storyClone.content_elements = contentElements
  if (options.setHeadlines) {
    storyClone.headlines = headlines
    storyClone.subheadlines = subheadlines
  }

  const clipboard = exportClipboardContent(clipboardContent(state))
  set(storyClone, 'additional_properties.clipboard', clipboard)

  const basicRelatedContent = draftRelatedContentToAns(
    relatedContent(state).data
  )
  set(storyClone, 'related_content.basic', basicRelatedContent)

  const { storyNote } = storyDataToAns(getStoryData(state))
  set(storyClone, 'planning.internal_note', storyNote)

  const plainText = getTextWithoutComments(getDraftEditorContentState(state))
  set(
    storyClone,
    'planning.story_length.word_count_actual',
    getWordCount(plainText)
  )
  set(
    storyClone,
    'planning.story_length.character_count_actual',
    getCharacterCount(plainText)
  )

  // https://www.npmjs.com/package/punycode#punycodeucs2decodestring
  set(storyClone, 'planning.story_length.character_encoding', 'UTF-16')

  const lineSettings = getLineSettings(state)
  const primaryWebsiteLineSettings =
    lineSettings.websites[storyClone.canonical_website] || lineSettings.default

  if (primaryWebsiteLineSettings.value) {
    if (primaryWebsiteLineSettings.unit) {
      const lineCount = getLineCount(plainText, primaryWebsiteLineSettings)
      set(storyClone, 'planning.story_length.line_count_actual', lineCount)
    }

    if (primaryWebsiteLineSettings.linesPerInch) {
      // Rounded up to fit the ANS schema, as well as to match the behavior
      // of inch_count_planned in Planning setting
      const inches = Math.ceil(getInches(plainText, primaryWebsiteLineSettings))
      set(storyClone, 'planning.story_length.inch_count_actual', inches)
    }
  }

  return storyClone
}

// Detect and parse out private Okta Urls
export function getPrivateOktaUrls(story: Story): string[] {
  const oktaUrlRegex = /(\\?)(?:https?:)\/\/[a-z0-9.-]+?.arcpublishing\.com[^ "<]*\1/gi
  const privateOktaUrls: string[] = []
  let urlMatches: string[] = []

  // verify headlines and subheadlines
  if (story.headlines) {
    urlMatches = JSON.stringify(story.headlines).match(oktaUrlRegex) || []
    if (urlMatches.length) {
      privateOktaUrls.push(...urlMatches)
    }
  }
  if (story.subheadlines) {
    urlMatches = JSON.stringify(story.subheadlines).match(oktaUrlRegex) || []
    if (urlMatches.length) {
      privateOktaUrls.push(...urlMatches)
    }
  }

  // verify only text, header and raw_html content elements
  if (story.content_elements) {
    story.content_elements.forEach((e) => {
      if (e.type === 'text' || e.type === 'header') {
        urlMatches = String(e.content).match(oktaUrlRegex) || []
      }
      if (urlMatches.length) {
        privateOktaUrls.push(...urlMatches)
      }
    })
  }

  // verify raw_html in featured media
  if (story.promo_items) {
    Object.values(story.promo_items).forEach((promoItem) => {
      if (promoItem.type === 'raw_html') {
        urlMatches = String(promoItem.content).match(oktaUrlRegex) || []
      }
      if (urlMatches.length) {
        privateOktaUrls.push(...urlMatches)
      }
    })
  }

  // clean up the `\"` part in stringified object. Example
  // Orginal {example:'hello <a href="http://hello.world/link">Wordl</a>'} -> Stringified {"example":"hello <a href=\\"http://hello.world/link\\">Wordl</a>"}
  // then test each url to remove valid urls
  // add to Set before converting back to array to remove multiple instances of the same URL
  return [
    ...new Set(privateOktaUrls.map((content) => content.replace(/\\?"/g, ''))),
  ]
}

export async function validateStory(
  story: Story,
  settings: ComposerSettings,
  editorFieldConfigs: EllipsisReduxStore['editorFields'],
  activeCirculations: Circulation[] | []
): ValidateStory {
  const errorMessages: string[] = []
  const warningMessages: string[] = []

  // User can't correct from rev history - need to bypass
  const bypassCirculationValidation = window.location.href.includes('/revision')

  if (settings.v2) {
    const missingFields = handleRequired(
      'requiredSave',
      settings.v2,
      story,
      editorFieldConfigs
    ).filter((field) => field.missing === true)

    for (const field of missingFields) {
      errorMessages.push(
        intl.get('story.save.validationMessages.missingField', {
          missingValue: field.label,
        })
      )
    }
  }

  if (!story.canonical_website) {
    errorMessages.push(
      story._id
        ? intl.get('story.save.validationMessages.primaryWebsiteMissing')
        : intl.get('story.save.validationMessages.primaryWebsite')
    )
  }

  if (
    settings.circulations?.requiredToSave &&
    !activeCirculations.length &&
    !bypassCirculationValidation
  ) {
    errorMessages.push(intl.get('story.save.validationMessages.circulations'))
  }

  if (story.redirect?.forwardUrl) {
    try {
      story.redirect.forwardUrl.startsWith('/')
        ? new URL(story.redirect.forwardUrl, window.location.origin)
        : new URL(story.redirect.forwardUrl)
    } catch (error) {
      errorMessages.push(intl.get('story.save.validateStory.redirectUrl'))
    }
  }

  const privateOktaUrls = getPrivateOktaUrls(story)

  if (privateOktaUrls.length) {
    const privateOktaUrlMessageHtml = `
    <div>${intl.get('story.save.validationMessages.privateOktaUrl')}</div>
    <ul class="private-url-list" data-testid="private-url-list">
      ${privateOktaUrls.reduce((accu, curr) => {
        return `${accu}<li>${curr}</li>`
      }, '')}
    </ul>`.trim()

    warningMessages.push(privateOktaUrlMessageHtml)
  }

  return { errorMessages, warningMessages }
}
